import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Quote, X } from 'lucide-react';
import { SITE_DATA } from '../data/Constants';
import { optimizeImage, generateSrcSet } from '../utils';
import SEO from '../components/SEO';
import Schema from '../components/Schema';

// --- TESTIMONIAL CARD COMPONENT ---
const TestimonialCard = ({ review }) => {
  const [isOpen, setIsOpen] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  return (
    <>
      <div
        onClick={() => setIsOpen(true)}
        className='group border border-primary relative bg-white p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-xl h-full flex flex-col'
      >
        <div className='absolute -top-5 left-8 w-10 h-10 bg-primary flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300'>
          <Quote className='w-5 h-5 text-white' fill='currentColor' />
        </div>

        <div className='mt-6 mb-6 flex-1'>
          <p className='font-sans text-gray-700 leading-relaxed line-clamp-4 group-hover:text-secondary transition-colors'>
            "{review.text}"
          </p>
          <span className='text-primary text-xs font-bold uppercase tracking-widest mt-4 inline-block translate-y-2 opacity-100 group-hover:translate-y-0 transition-all duration-300'>
            Read Full Review →
          </span>
        </div>

        <div className='border-t border-gray-300 pt-6 flex items-center justify-between'>
          <div>
            <h3 className='font-heading text-lg text-gray-900 uppercase tracking-wide font-bold'>
              {review.name}
            </h3>
            <p className='text-xs text-gray-700 uppercase tracking-widest font-bold'>
              {review.role}
            </p>
          </div>
          <div className='flex gap-1'>
            {[...Array(5)].map((_, i) => (
              <Star key={i} className='w-3 h-3 text-primary' fill='currentColor' />
            ))}
          </div>
        </div>
      </div>

      {/* REVIEW MODAL POPUP */}
      {isOpen && (
        <div className='fixed inset-0 z-9999 flex items-center justify-center p-4'>
          <div
            className='absolute inset-0 backdrop-blur-sm'
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          ></div>

          <div className='relative bg-white w-full max-w-2xl p-10 md:p-14 rounded-sm shadow-2xl overflow-y-auto max-h-[70vh] animate-in fade-in zoom-in duration-300 custom-w-scrollbar'>
            <button
              aria-label="Close testimonial"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              className='absolute top-4 right-4 z-20 p-2 bg-gray-100 hover:bg-primary hover:text-white rounded-full transition-colors'
            >
              <X className='w-5 h-5' />
            </button>

            <div className='flex flex-col items-center text-center'>
              <Quote className='w-12 h-12 text-primary/20 mb-6' fill='currentColor' />

              <p className='font-sans text-xl md:text-2xl text-gray-900 leading-relaxed mb-10'>
                "{review.text}"
              </p>

              <div className='border-t border-gray-300 pt-8 w-full'>
                <div className='font-heading text-2xl uppercase tracking-wide text-primary font-bold'>
                  {review.name}
                </div>
                <div className='text-sm text-gray-700 uppercase tracking-[0.2em] font-bold mt-2'>
                  {review.role}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// --- LAZY VIDEO COMPONENT ---
const LazyVideo = () => {
  const [isVisible, setIsVisible] = useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={videoRef} className='relative aspect-video w-full rounded-lg overflow-hidden shadow-2xl bg-black'>
      {isVisible ? (
        <iframe
          src='https://player.vimeo.com/video/812977112?background=1&autoplay=1&loop=1&muted=1&playsinline=1&controls=0&byline=0&title=0'
          className='absolute inset-0 w-full h-full scale-[1.02]'
          allow='autoplay; fullscreen; picture-in-picture'
          allowFullScreen
          title='Napoleon Lifestyle'
        ></iframe>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/30">
          <span className="sr-only">Loading Video...</span>
        </div>
      )}

      {/* Subtle Overlay to ensure text readability if you add any over video later */}
      <div className='absolute inset-0 bg-black/10 pointer-events-none'></div>
    </div>
  );
};

const HomePage = () => {
  const [activeId, setActiveId] = useState('gas');
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Fireplaces & Outdoor Living Experts"
        description="Transform your home with premium gas, electric, and outdoor fireplaces. Discover the best brands and expert installation at our Langley showroom."
        keywords="fireplaces, gas fireplaces, electric fireplaces, outdoor living, grills, Langley, Vancouver"
      />
      <Schema type="LocalBusiness" />
      {/* HERO ACCORDION */}
      <section className='relative w-full h-dvh pt-[72px] overflow-hidden bg-gray-900'>
        <h1 className='sr-only'>Mainland Fireplaces - Premium Fireplaces & Outdoor Living Since 2001</h1>
        <div className='absolute inset-0 w-full h-full z-0'>
          {SITE_DATA.categories.map((cat, index) => (
            <div
              key={cat.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${activeId === cat.id ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={optimizeImage(cat.image, 1200)}
                srcSet={generateSrcSet(cat.image)}
                sizes="100vw"
                alt={cat.title}
                width={1200}
                height={800}
                fetchPriority={index === 0 ? "high" : "auto"}
                className='w-full h-full object-cover opacity-70'
              />
              <div className='absolute inset-0 bg-linear-to-t from-black/90 via-transparent to-black/20'></div>
            </div>
          ))}
        </div>

        {/* TABS OVERLAY LAYER */}
        <div className='relative z-10 flex flex-col md:flex-row w-full h-full'>
          {SITE_DATA.categories.map((cat, index) => {
            const isActive = activeId === cat.id;

            return (
              <article
                key={cat.id}
                role='button'
                tabIndex={0}
                aria-label={`View ${cat.title} category — ${cat.description || cat.title}`}
                onMouseEnter={() => setActiveId(cat.id)}
                onClick={() => setActiveId(cat.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveId(cat.id); }}
                className={`
                  relative group cursor-pointer 
                  border-b md:border-b-0 md:border-r border-white/20
                  transition-[flex-grow] duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]
                  overflow-hidden
                  ${isActive
                    ? 'flex-4' // Active takes ~50% width
                    : 'flex-1 hover:bg-white/5' // Inactives share 50%
                  }
                `}
              >
                <div className='relative w-full h-full flex flex-col justify-end p-6 md:p-12 pb-12 md:pb-20'>
                  {/* Vertical Label (Visible when Inactive) */}
                  <div
                    className={`
                      absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                      transition-all duration-500 delay-100
                      ${isActive ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'}
                    `}
                  >
                    <h2 className='font-heading text-lg md:text-2xl text-white uppercase tracking-[0.3em] vertical-text whitespace-nowrap md:rotate-180 font-bold group-hover:text-white transition-colors'>
                      {cat.title}
                    </h2>
                  </div>

                  {/* Expanded Content (Visible when Active) */}
                  <div
                    className={`
                      flex flex-col items-start transition-all duration-700 delay-200
                      ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 invisible'}
                    `}
                  >
                    <div className='flex items-center gap-3 text-primary mb-4'>
                      <span className='h-[1.5px] w-8 bg-primary'></span>
                      <span className='text-sm font-bold tracking-widest'>0{index + 1}</span>
                    </div>

                    <h2 className='font-heading text-4xl md:text-7xl lg:text-8xl text-white uppercase leading-none mb-6 drop-shadow-xl'>
                      {cat.title}
                    </h2>

                    <div className='max-w-xl'>
                      <h3 className='text-white/90 uppercase tracking-[0.2em] text-xs font-bold mb-6 font-sans'>
                        {cat.subtitle}
                      </h3>

                      <p className='font-sans text-white/90 text-base md:text-lg leading-relaxed mb-8 border-l-2 border-primary pl-6 hidden md:block'>
                        {cat.description}
                      </p>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products?category=${encodeURIComponent(cat.title)}`);
                        }}
                        className='group flex items-center gap-4 px-8 py-4 bg-primary text-white hover:bg-white hover:text-primary transition-all duration-300 shadow-lg uppercase font-bold tracking-widest text-xs rounded-sm'
                      >
                        Explore Collection
                        <ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* NAPOLEON BRAND SPOTLIGHT */}
      <section className='w-full bg-primary text-white py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10'>
          <div className='flex-1 space-y-8 animate-fade-in'>
            <div className='relative'>
              <div className='flex items-center gap-4 mb-4'>
                <span className='h-px w-12 bg-white'></span>
                <span className='text-sm uppercase tracking-[0.2em] text-white'>
                  Premium Partner
                </span>
                <span className='h-px w-12 bg-white'></span>
              </div>
              <h2 className='font-heading text-5xl md:text-7xl uppercase text-white leading-none drop-shadow-sm'>
                Featured <span className='text-secondary'>Brand</span>
              </h2>
            </div>

            <img
              src={`${import.meta.env.BASE_URL}images/brands/napoleon.svg`}
              alt='Napoleon Logo'
              width={192}
              height={48}
              className='w-48'
            />

            <p className='font-sans text-white text-lg leading-relaxed max-w-xl border-l border-white pl-6'>
              For over 40 years, Napoleon has led the way with innovative engineering and stunning design. From high-efficiency gas fireplaces to state-of-the-art electric models.
            </p>

            <div className='pt-4'>
              <button
                onClick={() => navigate('/napoleon')}
                className='group bg-white text-primary px-8 py-4 uppercase font-bold tracking-widest text-xs rounded-xl hover:bg-secondary hover:text-white transition-all duration-300 shadow-xl flex items-center gap-3'
              >
                Explore Collection
                <span className='group-hover:translate-x-1 transition-transform duration-300'>→</span>
              </button>
            </div>
          </div>

          <div className='flex-1 w-full relative group'>
            <div className='absolute -inset-4 border border-white/50 rounded-xl translate-x-2 translate-y-2 opacity-50 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-700'></div>
            <div className='absolute -inset-4 border border-white/50 rounded-xl -translate-x-2 -translate-y-2 opacity-50 group-hover:-translate-x-1 group-hover:-translate-y-1 transition-transform duration-700'></div>
            <LazyVideo />
          </div>

        </div>
      </section>

      {/* OUTDOOR FIRE FEATURES SPOTLIGHT */}
      <section className='w-full py-24 px-6 md:px-12 lg:px-24 bg-white border-b border-gray-100'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16 max-w-3xl mx-auto'>
            <div className='flex items-center justify-center gap-4 mb-4'>
              <span className='h-px w-12 bg-primary'></span>
              <span className='text-sm font-bold text-primary tracking-[0.2em] uppercase'>
                Outdoor Living
              </span>
              <span className='h-px w-12 bg-primary'></span>
            </div>

            <h2 className='font-heading text-5xl md:text-7xl uppercase text-secondary leading-none mb-6'>
              Extend <span className='text-primary'>The Season</span>
            </h2>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {SITE_DATA.outdoorFeatures.map((feat, i) => (
              <div key={i} className='group cursor-pointer' onClick={() => {
                //const categoryPath = `Outdoor Fire Features,${feat.title}`;
                //navigate(`/products?category=${encodeURIComponent(categoryPath)}`);
                navigate(`/products?q=${encodeURIComponent(feat.title)}`);
              }}>
                <div className='relative aspect-3/4 overflow-hidden mb-6 bg-gray-100 rounded-xs'>
                  <img
                    src={optimizeImage(feat.image, 600)}
                    srcSet={generateSrcSet(feat.image)}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    alt={feat.title}
                    width={600}
                    height={800}
                    loading="lazy"
                    className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105'
                  />
                  <div className='absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors'></div>
                  <div className='absolute top-4 left-4 bg-white/90 backdrop-blur-xs p-3 rounded-full text-primary'>
                    {feat.icon}
                  </div>
                </div>

                <h3 className='font-heading text-2xl text-secondary uppercase mb-2 group-hover:text-primary transition-colors'>
                  {feat.title}
                </h3>

                <p className='font-sans text-gray-700 text-sm leading-relaxed mb-4'>
                  {feat.desc}
                </p>

                <div className='flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-secondary border-b border-secondary pb-1 w-max group-hover:text-primary group-hover:border-primary transition-all'>
                  View Collection <ArrowRight className='w-3 h-3' />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS CAROUSEL */}
      <section className='w-full py-24 bg-primary overflow-hidden relative isolate'>
        <div className='max-w-7xl mx-auto px-6 mb-16 text-center'>
          <div className='flex items-center justify-center gap-4 mb-4'>
            <span className='h-px w-12 bg-white'></span>
            <span className='text-sm uppercase tracking-[0.2em] text-white'>
              Industry Leaders
            </span>
            <span className='h-px w-12 bg-white'></span>
          </div>
          <h2 className='font-heading text-4xl md:text-5xl uppercase text-white tracking-wide drop-shadow-sm'>
            Brands We <span className='text-secondary'>Trust</span>
          </h2>
        </div>

        <div className='relative w-full max-w-[100vw] overflow-hidden'>
          <div className='absolute inset-y-0 left-0 w-32 bg-linear-to-r from-primary to-transparent z-10 pointer-events-none'></div>
          <div className='absolute inset-y-0 right-0 w-32 bg-linear-to-l from-primary to-transparent z-10 pointer-events-none'></div>

          <div className='flex w-max animate-scroll gap-16 md:gap-32 items-center'>
            {[...SITE_DATA.brands, ...SITE_DATA.brands, ...SITE_DATA.brands].map((brand, i) => (
              <div
                key={i}
                className='relative group flex flex-col items-center justify-center cursor-pointer px-4'
              >
                <img
                  src={brand.logo}
                  alt={`${brand.name} Logo`}
                  width={160}
                  height={64}
                  loading="lazy"
                  className='h-12 md:h-16 w-auto object-contain brightness-0 invert opacity group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-110'
                />

                <div className='absolute -inset-4 bg-white/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full -z-10'></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className='w-full py-24 bg-white relative border-b border-gray-100'>
        <div className='max-w-7xl mx-auto px-6 relative z-10'>

          <div className='text-center mb-20'>
            <div className='flex items-center justify-center gap-4 mb-4'>
              <span className='h-px w-12 bg-primary'></span>
              <span className='text-sm font-bold text-primary tracking-[0.2em] uppercase'>
                Client Stories
              </span>
              <span className='h-px w-12 bg-primary'></span>
            </div>
            <h2 className='font-heading text-5xl md:text-7xl uppercase text-secondary leading-none mb-6'>
              Trusted Since <span className='text-primary'>2001</span>
            </h2>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {SITE_DATA.testimonials.map((review, index) => (
              <TestimonialCard key={index} review={review} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className='w-full py-24 bg-gray-50 border-b border-gray-100'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='text-center mb-16'>
            <div className='flex items-center justify-center gap-4 mb-4'>
              <span className='h-px w-12 bg-primary'></span>
              <span className='text-sm font-bold text-primary tracking-[0.2em] uppercase'>
                Questions
              </span>
              <span className='h-px w-12 bg-primary'></span>
            </div>
            <h2 className='font-heading text-4xl md:text-6xl uppercase text-secondary leading-none mb-6'>
              Frequently <span className='text-primary'>Asked</span>
            </h2>
          </div>

          <div className='space-y-4'>
            {[
              {
                q: "Do you offer installation services for fireplaces and grills?",
                a: "Yes! Mainland Fireplaces has in-house, certified installation teams. We handle everything from the initial consultation and permitting to the final professional installation and finishing touches."
              },
              {
                q: "What areas do you serve?",
                a: "We are based in Langley, BC and provide sales, service and installation throughout the lower mainland and other parts of BC such as the Interior, Island and Northern region."
              },
              {
                q: "What is the difference between direct vent and vent-free gas fireplaces?",
                a: "Direct vent fireplaces draw air from outside and exhaust combustion gases outside, making them highly efficient and safe for tightly sealed homes. Vent-free fireplaces draw air from inside the room and vent into the room; note that vent-free units are generally not permitted for use in Canada under current building codes."
              },
              {
                q: "Do electric fireplaces actually provide heat?",
                a: "Yes, modern electric fireplaces include built-in heaters (typically 1,500W to 3,000W) that can effectively warm rooms up to 400-800 square feet, depending on the model and voltage."
              }
            ].map((faq, index) => (
              <details key={index} className='group bg-white border border-gray-200 p-6 rounded-sm shadow-sm [&_summary::-webkit-details-marker]:hidden'>
                <summary className='flex items-center justify-between cursor-pointer font-heading text-lg md:text-xl text-secondary uppercase font-bold group-open:text-primary transition-colors'>
                  {faq.q}
                  <span className='ml-4 flex-shrink-0 bg-gray-100 w-8 h-8 flex items-center justify-center rounded-full text-primary group-open:rotate-45 transition-transform duration-300'>+</span>
                </summary>
                <div className='mt-4 pt-4 border-t border-gray-100 font-sans text-gray-600 leading-relaxed text-base md:text-lg animate-fade-in'>
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
      <Schema type="AggregateRating" data={{ reviews: SITE_DATA.testimonials }} />
      <Schema type="FAQPage" data={{
        questions: [
          { question: "Do you offer installation services for fireplaces and grills?", answer: "Yes! Mainland Fireplaces has in-house, certified installation teams. We handle everything from the initial consultation and permitting to the final professional installation and finishing touches." },
          { question: "What areas do you serve?", answer: "We are based in Langley, BC and provide sales, service and installation throughout the lower mainland and other parts of BC such as the Interior, Island and Northern region." },
          { question: "What is the difference between direct vent and vent-free gas fireplaces?", answer: "Direct vent fireplaces draw air from outside and exhaust combustion gases outside, making them highly efficient and safe for tightly sealed homes. Vent-free fireplaces draw air from inside the room and vent into the room; note that vent-free units are generally not permitted for use in Canada under current building codes." },
          { question: "Do electric fireplaces actually provide heat?", answer: "Yes, modern electric fireplaces include built-in heaters (typically 1,500W to 3,000W) that can effectively warm rooms up to 400-800 square feet, depending on the model and voltage." }
        ]
      }} />
    </>
  );
};

export default HomePage;